import React, {
  useCallback, useEffect, useMemo,
} from 'react';

import { useControllableState } from './hooks/useControllableState';
import { SIDEBAR_MOBILE_QUERY, useMediaQuery } from './hooks/useMediaQuery';
import type { SidebarCtx } from './SidebarContext';
import { SidebarContext } from './SidebarContext';

// ============================================================
//     TYPES
// ============================================================
interface SidebarProviderProps {
  children: React.ReactNode;
  /**
   * Whether the sidebar should initially render in an open state.
   *
   * Does not control subsequent interaction with the sidebar, only the initial render.
   *
   * @default true
   */
  defaultExpanded?: boolean;
  /**
   * Used to control the state of the sidebar externally.
   *
   * **NOTE**: Must be used in conjunction with `onExpandedChange`
   */
  expanded?: boolean;
  /**
   * Whether the sidebar can be collapsed
   *
   * @default false
   */
  isCollapsible?: boolean;
  /**
   * Whether the sidebar should render in a special mobile mode under certain conditions
   *
   * When `false`, the sidebar will never render in a mobile mode, no matter the value of `shouldShowMobile`
   *
   * @default false
   */
  isMobileEnabled?: boolean;
  /**
   * Used to control the state of the mobile sidebar externally.
   *
   * **NOTE**: Must be used in conjunction with `onOpenMobileChange`
   */
  isOpenMobile?: boolean;
  /**
   * Trigger opening the sidebar via a global keyboard shortcut
   *
   * NOTE: If rendering multiple sidebars at the same time, be sure to assign each a unique shortcut
   */
  keyboardShortcut?: {
    /**
     * Which key should trigger the sidebar
     */
    key: string;

    /**
     * Whether the command key (`⌘`) or control key (`ctrl`) should be pressed as well
     *
     * @default true
     */
    withMetaKey?: boolean;
  };
  /**
   * Callback for triggering side effects on sidebar state change.
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * Callback for triggering side effects on mobile sidebar state change.
   */
  onOpenMobileChange?: (open: boolean) => void;
  /**
   * Whether the mobile sidebar should be shown
   *
   * If not provided, the mobile sidebar will be shown on screen sizes below 768px
   */
  shouldShowMobile?: boolean;
}

const NoOp = () => {};

// ============================================================
//     PROVIDER
// ============================================================
/**
 * Provides context for sidebar state and actions
 *
 * Ported from the Frontend-Platform Sidebar provider, including mobile drawer support.
 * `isMobile` is only ever `true` when `isMobileEnabled` is set (see `Sidebar_v2`'s `SideNav`,
 * the only consumer that opts in today).
 */
export const SidebarProvider = (props: SidebarProviderProps) => {
  const {
    children,
    defaultExpanded = true,
    expanded: expandedProp,
    isCollapsible = false,
    isMobileEnabled = false,
    isOpenMobile: isOpenMobileProp,
    keyboardShortcut,
    onExpandedChange: onExpandedChangeProp,
    onOpenMobileChange: onOpenMobileChangeProp,
    shouldShowMobile: shouldShowMobileProp,
  } = props;

  const isMobileQuery = useMediaQuery(SIDEBAR_MOBILE_QUERY);
  /** Whether the sidebar is currently in mobile mode */
  const isMobile = isMobileEnabled && (shouldShowMobileProp ?? isMobileQuery);

  const [isExpanded, setIsExpanded] = useControllableState({
    defaultProp: defaultExpanded,
    prop: isCollapsible ? expandedProp : true,
    onChange: isCollapsible ? onExpandedChangeProp : NoOp,
  });

  const [isOpenMobile, setIsOpenMobile] = useControllableState({
    defaultProp: false,
    prop: isOpenMobileProp,
    onChange: onOpenMobileChangeProp,
  });

  // Helper to toggle the sidebar.
  const toggleSidebar = useCallback(() => {
    if (isMobile) {
      setIsOpenMobile(old => !old);
    } else {
      setIsExpanded(old => !old);
    }
  }, [isMobile, setIsExpanded, setIsOpenMobile]);

  // Close the mobile sidebar state when the sidebar is no longer in mobile mode
  useEffect(() => {
    if (isOpenMobile && !isMobile) {
      setIsOpenMobile(false);
    }
  }, [isMobile, isOpenMobile, setIsOpenMobile]);

  // Handles a keyboard shortcut to toggle the sidebar
  useEffect(() => {
    if (!keyboardShortcut) return undefined;

    const { key, withMetaKey = true } = keyboardShortcut;

    const handleKeyDown = (event: KeyboardEvent) => {
      const shortcutKeyPressed = event.key === key;
      const metaKeyPressed = withMetaKey ? event.metaKey || event.ctrlKey : true;

      if (shortcutKeyPressed && metaKeyPressed) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [keyboardShortcut, toggleSidebar]);

  const contextValue = useMemo<SidebarCtx>(
    () => ({
      isExpanded: isMobile ? true : isExpanded,
      isMobile,
      isOpenMobile,
      setIsExpanded,
      setIsOpenMobile,
      toggleSidebar,
    }),
    [isExpanded, isMobile, isOpenMobile, setIsExpanded, setIsOpenMobile, toggleSidebar],
  );

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>;
};

SidebarProvider.displayName = 'Sidebar.Provider' as const;
